import { LEGACY_TERMS } from '../terms/legacyTerms';
function str(v) {
    return v != null ? String(v).trim() : '';
}
function signedAmount(row) {
    var _a, _b;
    const extended = row;
    if (extended.signedAmount != null && Number.isFinite(Number(extended.signedAmount))) {
        return Number(extended.signedAmount);
    }
    const lt = String((_b = (_a = extended.ledgerType) !== null && _a !== void 0 ? _a : row.type) !== null && _b !== void 0 ? _b : '').toLowerCase();
    const amt = Math.abs(Number(row.amount) || 0);
    if (lt === 'deposit' || lt === 'credit')
        return amt;
    if (lt === 'withdraw' || lt === 'debit')
        return -amt;
    return Number(row.amount) || 0;
}
function roleNoun(party) {
    const role = str((party === null || party === void 0 ? void 0 : party.roleLabel) || (party === null || party === void 0 ? void 0 : party.ownerType)).toLowerCase();
    if (!role)
        return 'account';
    if (role === 'super agent' || role === 'super_agent')
        return 'super agent';
    if (role === 'game provider' || role === 'game_provider')
        return 'game provider';
    return role.replace(/_/g, ' ');
}
function roleTitle(party, terms) {
    return terms.credit.partyRole(roleNoun(party));
}
function partyName(party) {
    return str(party === null || party === void 0 ? void 0 : party.displayName) || str(party === null || party === void 0 ? void 0 : party.label) || str(party === null || party === void 0 ? void 0 : party.ownerId) || 'unknown';
}
function shortParty(party, terms) {
    if (!party)
        return 'unknown';
    const name = str(party.displayName);
    if (name)
        return name;
    return roleTitle(party, terms);
}
function isUsdConversion(row) {
    var _a;
    const refType = str((_a = row.referenceType) !== null && _a !== void 0 ? _a : row.reference_type).toLowerCase();
    const desc = str(row.description).toLowerCase();
    return (refType === 'usd_wallet_transaction' ||
        desc.includes('balance to credits') ||
        desc.includes('balance-to-credits'));
}
function isGameProviderParty(party) {
    const t = str(party === null || party === void 0 ? void 0 : party.ownerType).toLowerCase();
    return t === 'game_provider' || t === 'game provider';
}
function activity(owner, action) {
    return `${owner} - ${action}`;
}
function signupBonusKind(row) {
    var _a, _b;
    const txn = str((_a = row.transactionId) !== null && _a !== void 0 ? _a : row.transaction_id).toLowerCase();
    const refType = str((_b = row.referenceType) !== null && _b !== void 0 ? _b : row.reference_type).toLowerCase();
    const desc = str(row.description).toLowerCase();
    if (txn.startsWith('signup_bonus:claim:') || desc.includes('signup bonus (welcome'))
        return 'claim';
    if (txn.startsWith('signup_bonus:expire:') ||
        desc.includes('signup bonus expired') ||
        desc.includes('recall grant credits')) {
        return 'expire';
    }
    if (refType === 'signup_bonus' && txn.includes('claim'))
        return 'claim';
    if (refType === 'signup_bonus' && txn.includes('expire'))
        return 'expire';
    return null;
}
function platformParty() {
    return {
        ownerType: 'platform',
        ownerId: 'platform',
        displayName: 'Platform',
        roleLabel: 'Platform',
        label: 'Platform: Platform',
    };
}
function inferGameProviderParty(row) {
    const desc = str(row.description);
    const m = desc.match(/withdraw(?:al)? from (.+?) game wallet/i) ||
        desc.match(/deposit(?:ed)? to (.+?) game wallet/i) ||
        desc.match(/(?:from|to) (.+?) game wallet/i);
    if (!(m === null || m === void 0 ? void 0 : m[1]))
        return null;
    const name = m[1].trim();
    return {
        ownerType: 'game_provider',
        ownerId: name,
        displayName: name,
        roleLabel: 'Game Provider',
        label: `Game Provider: ${name}`,
    };
}
/**
 * Human activity for credit ledger rows (who did what / money direction).
 */
export function creditLedgerActivityLabel(row, terms = LEGACY_TERMS) {
    var _a, _b, _c, _d;
    const signed = signedAmount(row);
    const isIn = signed > 1e-9;
    const isOut = signed < -1e-9;
    const type = str(row.type).toLowerCase();
    const ledgerType = str(row.ledgerType).toLowerCase();
    const deposit = ledgerType === 'deposit' || type === 'credit' || isIn;
    const withdraw = ledgerType === 'withdraw' || type === 'debit' || isOut;
    const bonusKind = signupBonusKind(row);
    const inferredGame = inferGameProviderParty(row);
    const from = (_b = (_a = row.fromParty) !== null && _a !== void 0 ? _a : (deposit ? inferredGame : null)) !== null && _b !== void 0 ? _b : (bonusKind === 'claim' && deposit ? platformParty() : null);
    const to = (_d = (_c = row.toParty) !== null && _c !== void 0 ? _c : (withdraw ? inferredGame : null)) !== null && _d !== void 0 ? _d : (bonusKind === 'expire' && withdraw ? platformParty() : null);
    if (bonusKind === 'claim') {
        if (deposit) {
            return activity(roleTitle(to, terms) || 'Player', 'receive signup bonus');
        }
        if (withdraw) {
            const dest = to ? shortParty(to, terms) : 'player';
            return activity(roleTitle(from, terms) || 'Shop', `fund signup bonus to ${dest}`);
        }
    }
    if (bonusKind === 'expire') {
        if (withdraw) {
            if (to && str(to.ownerType).toLowerCase() === 'shop') {
                return activity(roleTitle(from, terms) || 'Player', `return expired signup bonus to ${shortParty(to, terms)}`);
            }
            return activity(roleTitle(from, terms) || 'Player', 'return expired signup bonus');
        }
        if (deposit) {
            return activity(roleTitle(to, terms) || 'Shop', 'receive returned signup bonus');
        }
    }
    if (isUsdConversion(row)) {
        if (deposit) {
            return activity(roleTitle(to, terms) || 'Account', 'receive credits from USD conversion');
        }
        return activity(roleTitle(from, terms) || 'Account', 'fund credits from USD conversion');
    }
    if (from && to && (isGameProviderParty(from) || isGameProviderParty(to))) {
        if (isGameProviderParty(from) && deposit) {
            return activity(roleTitle(to, terms), `recall credits from ${shortParty(from, terms)}`);
        }
        if (isGameProviderParty(to) && withdraw) {
            return activity(roleTitle(from, terms), `deposit credits to ${shortParty(to, terms)}`);
        }
    }
    if (from && to) {
        if (deposit) {
            return activity(roleTitle(to, terms), `receive credits from ${shortParty(from, terms)}`);
        }
        if (withdraw) {
            return activity(roleTitle(from, terms), `send credits to ${shortParty(to, terms)}`);
        }
        return activity(roleTitle(from, terms), `transfer credits to ${shortParty(to, terms)}`);
    }
    if (deposit && to) {
        const src = from ? shortParty(from, terms) : 'another account';
        return activity(roleTitle(to, terms), `receive credits from ${src}`);
    }
    if (withdraw && from) {
        const dest = to ? shortParty(to, terms) : 'another account';
        return activity(roleTitle(from, terms), `send credits to ${dest}`);
    }
    if (deposit)
        return 'Credits received';
    if (withdraw)
        return 'Credits sent';
    return 'Credit wallet activity';
}
/** Prefer API party label; fall back to game-provider hints in description. */
export function creditPartyDisplayLabel(party, row, terms = LEGACY_TERMS) {
    var _a, _b, _c;
    if (party === null || party === void 0 ? void 0 : party.label)
        return terms.credit.partyText(party.label);
    if (party) {
        const role = (_a = party.roleLabel) !== null && _a !== void 0 ? _a : 'Account';
        const name = (_c = (_b = party.displayName) !== null && _b !== void 0 ? _b : party.ownerId) !== null && _c !== void 0 ? _c : 'Unknown';
        return terms.credit.partyText(`${role}: ${name}`);
    }
    if (!row)
        return '—';
    const desc = str(row.description);
    const m = desc.match(/withdraw(?:al)? from (.+?) game wallet/i) ||
        desc.match(/deposit(?:ed)? to (.+?) game wallet/i) ||
        desc.match(/(?:from|to) (.+?) game wallet/i);
    if (m === null || m === void 0 ? void 0 : m[1])
        return `${terms.credit.partyRole('game provider')}: ${m[1].trim()}`;
    const bonusKind = signupBonusKind(row);
    if (bonusKind === 'claim' || bonusKind === 'expire')
        return 'Platform: Platform';
    return '—';
}
